import { InfoModal } from '@shared/layout/Modal'
import { FC, useEffect, useState } from 'react'
import { usePWAInstall } from '@shared/hooks/useInstallPwa'
import { getFromLocalStorage, setToLocalStorage } from '@shared/lib/storage'
import { useAlert } from '@app/ToastProvider'
import { useIonRouter, UseIonRouterResult } from '@ionic/react'
import { ROUTES } from '@shared/constants/routes'
import { isPwaMode } from '@shared/lib/system/isPwaMode'
import { Capacitor } from '@capacitor/core'
import { LocalStorageKeys } from '@shared/constants/LocalStorageKeys'

interface PwaInstallationModalProps {
	isOpen?: boolean
	onClose?: () => void
}

export const PwaInstallationModal: FC<PwaInstallationModalProps> = ({ isOpen, onClose }) => {
	const [showModal, setShowModal] = useState<boolean>(false)
	const { handleInstallClick } = usePWAInstall({
		onError: () => {
			useAlert({ message: 'Невозможно установить мобильную версию', header: 'Ошибка' })
		},
	})
	const nav: UseIonRouterResult = useIonRouter()

	if (Capacitor.isNativePlatform() || isPwaMode()) {
		return null
	}

	const disallowPwaModal = async () => {
		await setToLocalStorage(LocalStorageKeys.wasPwaModalSuggested, 'NO')
	}

	useEffect(() => {
		if (typeof isOpen !== 'undefined') {
			setShowModal(isOpen)
			return
		}

		const getIsPwaModalAvailable = async () => {
			const isAvailable: string | object | null =
				await getFromLocalStorage(LocalStorageKeys.wasPwaModalSuggested)
			if (typeof isAvailable === 'string') {
				setShowModal(false)
			} else {
				setShowModal(true)
			}
		}

		getIsPwaModalAvailable()
	}, [isOpen])

	useEffect(() => {
		if (!showModal) {
			if (onClose) onClose()
		}
	}, [showModal])

	const isMacOrIOS = () => {
		const userAgent = navigator.userAgent
		const isIOS: boolean = /iPhone|iPad|iPod/i.test(userAgent)
		const isMac: boolean = /Macintosh/i.test(userAgent) && !isIOS
		return isIOS || isMac
	}

	const onInstallButtonClick = async () => {
		if (isMacOrIOS()) {
			nav.push(ROUTES.overall.pwaInstallationGuide)
			return
		}

		await disallowPwaModal()

		handleInstallClick().then(() => {
			setShowModal(false)
		})
	}

	const onRefuseInstallButtonClick = async () => {
		await disallowPwaModal()
		setShowModal(false)
	}

	return (
		<>
			{showModal && (
				<InfoModal
					contentStyle={{
						width: 'min(calc(100vw - 56px), 340px)',
						minHeight: 184,
						height: 'auto',
						padding: '24px 18px 18px',
						borderRadius: 22,
					}}
					overlayStyle={{
						backgroundColor: 'rgba(17, 17, 19, 0.32)',
						backdropFilter: 'blur(6px)',
						WebkitBackdropFilter: 'blur(6px)',
					}}
					setShowModal={setShowModal}
					buttonsAmount={2}
					firstButtonText={'Не сейчас'}
					onFirstButtonClick={onRefuseInstallButtonClick}
					secondButtonText={'Установить'}
					onSecondButtonClick={onInstallButtonClick}
					onClose={() => {
						void disallowPwaModal()
					}}
					titleStyle={{
						fontSize: 22,
						fontWeight: 600,
						lineHeight: 1.16,
						letterSpacing: 0,
						padding: '2px 36px 0',
					}}
					descriptionStyle={{
						fontSize: 15,
						lineHeight: 1.3,
						fontWeight: 400,
						marginTop: 12,
						padding: '0 2px',
					}}
					firstButtonStyles={{
						width: 126,
						height: 46,
						fontSize: 17,
						background: '#F0F1F0',
						color: '#202124',
					}}
					secondButtonStyles={{
						width: 126,
						height: 46,
						fontSize: 17,
						fontWeight: 500,
					}}
					title={'Установить приложение?'}
					description={
						'Иконка появится на главном экране, и приложение будет открываться в один тап.'
					}
				/>
			)}
		</>
	)
}
